import type { MouseEvent as ReactMouseEvent, TouchEvent as ReactTouchEvent } from 'react';
import { useEffect, useRef } from 'react';

/** Pixels a finger may drift and still be holding rather than scrolling. */
const MOVE_TOLERANCE = 10;

/**
 * Half a second on a row, as a gesture a list can be given.
 *
 * Holding a row is how a phone asks for the checkboxes, and there is more to
 * getting it right than a timer. Scrolling the list is also a finger held down
 * on a row, so a press that travels is abandoned rather than counted. The menu
 * Android offers for a long press arrives on top of the one the row is asking
 * for, so the context menu is refused. And the click that ends the hold would
 * immediately unpick the row the hold just picked, so the press leaves a mark
 * the click reads and clears.
 *
 * The row still has to carry `select-none [-webkit-touch-callout:none]`: half a
 * second on a row is also what Android's WebView reads as "select this text",
 * and that wins — the handles come up and the row's own timer never gets its
 * press.
 */
export function useLongPress(onHold: (id: string) => void, delayMs = 500) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Where the finger went down, so a scroll can be told from a press. */
  const origin = useRef<{ x: number; y: number } | null>(null);
  /** Set when the press won, so the click that follows is not a second toggle. */
  const fired = useRef(false);

  const cancel = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    origin.current = null;
  };

  useEffect(() => cancel, []);

  const start = (id: string, at?: { x: number; y: number }) => {
    origin.current = at ?? null;
    timer.current = setTimeout(() => {
      fired.current = true;
      onHold(id);
    }, delayMs);
  };

  const move = (at: { x: number; y: number }) => {
    const from = origin.current;
    if (!from) return;
    if (Math.abs(at.x - from.x) > MOVE_TOLERANCE || Math.abs(at.y - from.y) > MOVE_TOLERANCE) cancel();
  };

  /** True once, for the click a finished hold ends with. */
  const consumeClick = (): boolean => {
    if (!fired.current) return false;
    fired.current = false;
    return true;
  };

  /*
   * Spread onto the row. Everything here is about the press, nothing about the
   * row.
   *
   * React's own event types rather than the shapes these handlers actually
   * read: a structural `{ touches: TouchList }` names the DOM's `TouchList`,
   * which React's synthetic one is not — it has no `Symbol.iterator` — and the
   * whole spread then fails to satisfy the div it is spread onto. The
   * mismatch is invisible until the object meets an element, so it surfaced on
   * the two rows that use this and not in the hook.
   */
  const handlers = (id: string) => ({
    onContextMenu: (event: ReactMouseEvent) => event.preventDefault(),
    onMouseDown: (event: ReactMouseEvent) => start(id, { x: event.clientX, y: event.clientY }),
    onMouseMove: (event: ReactMouseEvent) => move({ x: event.clientX, y: event.clientY }),
    onMouseUp: cancel,
    onMouseLeave: cancel,
    onTouchStart: (event: ReactTouchEvent) => {
      const touch = event.touches[0];
      start(id, touch ? { x: touch.clientX, y: touch.clientY } : undefined);
    },
    onTouchMove: (event: ReactTouchEvent) => {
      const touch = event.touches[0];
      if (touch) move({ x: touch.clientX, y: touch.clientY });
    },
    onTouchEnd: cancel,
    onTouchCancel: cancel,
  });

  return { handlers, consumeClick, cancel };
}
